// Adding, reading and removing `field:value` terms of the query, for facets, columns and the inspector's buttons.
const quote = (value: string): string => (/[\s()"]/.test(value) || value === '' ? `"${value.replace(/"/g, '\\"')}"` : value);

export function termOf(field: string, value: string): string {
    return `${field}:${quote(value)}`;
}

// The query's parts, a quoted value kept whole: `-module:"Cron Job"` is one part.
function parts(q: string): string[] {
    return q.match(/-?[\w.@$-]*:?"(?:[^"\\]|\\.)*"|\S+/g) ?? [];
}

const same = (part: string, term: string): boolean => part.replace(/^-/, '').toLowerCase() === term.toLowerCase();

export function termState(q: string, field: string, value: string): 'inc' | 'exc' | null {
    const term = termOf(field, value);
    for (const part of parts(q)) {
        if (same(part, term)) {
            return part.startsWith('-') ? 'exc' : 'inc';
        }
    }

    return null;
}

// The term replaces any earlier mention of the same field and value, kept or excluded.
export function withTerm(q: string, field: string, value: string, exclude: boolean): string {
    const term = termOf(field, value);
    const rest = parts(q).filter((part) => !same(part, term));

    return [...rest, `${exclude ? '-' : ''}${term}`].join(' ');
}

export function withoutTerm(q: string, field: string, value: string): string {
    const term = termOf(field, value);

    return parts(q)
        .filter((part) => !same(part, term))
        .join(' ');
}

// Every `field:…` term of the query for one field, kept or excluded: a facet's clear button removes them all.
export function withoutField(q: string, field: string): string {
    const prefix = `${field.toLowerCase()}:`;

    return parts(q)
        .filter((part) => !part.replace(/^-/, '').toLowerCase().startsWith(prefix))
        .join(' ');
}

export const hasField = (q: string, field: string): boolean =>
    parts(q).some((part) => part.replace(/^-/, '').toLowerCase().startsWith(`${field.toLowerCase()}:`));
